import React, { useState, useCallback } from 'react';
import { Input } from './Input';
import { Button } from './Button';
import { Text } from './Text';
import showErrorToast from './Toasts/ShowErrorToast';

interface FileWithPreview {
    file: File;
    id: string;
    preview?: string;
    type: 'image' | 'pdf';
}

interface MultipleImageUploadProps {
    files: FileWithPreview[];
    onFilesChange: (files: FileWithPreview[]) => void;
    maxFiles?: number;
    maxFileSize?: number; // Maximum file size in MB
    accept?: string;
    className?: string;
}

const MultipleImageUpload: React.FC<MultipleImageUploadProps> = ({
    files,
    onFilesChange,
    maxFiles = 5,
    maxFileSize = 50, // Default 50MB
    accept = "image/*,.pdf",
    className = ""
}) => {
    const [dragActive, setDragActive] = useState(false);
    const maxFileSizeBytes = maxFileSize * 1024 * 1024;

    const getFileType = (file: File): 'image' | 'pdf' => {
        return file.type === 'application/pdf' ? 'pdf' : 'image';
    };

    const createFilePreview = (file: File): FileWithPreview => {
        const type = getFileType(file);
        const id = `${file.name}-${Date.now()}-${Math.random()}`;

        let preview: string | undefined;
        if (type === 'image') {
            preview = URL.createObjectURL(file);
        }

        return { file, id, preview, type };
    };

    const handleFileSelect = useCallback((fileList: FileList | null) => {
        if (!fileList) return;

        const validFiles: File[] = [];
        const errors: string[] = [];

        Array.from(fileList).forEach(file => {
            // Check file type
            const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
            if (!isValidType) {
                errors.push(`"${file.name}" - Invalid file type. Only images and PDFs are allowed.`);
                return;
            }

            // Check file size
            if (file.size > maxFileSizeBytes) {
                const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
                errors.push(`"${file.name}" (${fileSizeMB}MB) - Exceeds maximum file size of ${maxFileSize}MB.`);
                return;
            }

            // Check for duplicates
            const isDuplicate = files.some(existing =>
                existing.file.name === file.name && existing.file.size === file.size
            );
            if (isDuplicate) {
                errors.push(`"${file.name}" - File already selected.`);
                return;
            }

            validFiles.push(file);
        });

        // Show errors if any
        if (errors.length > 0) {
            showErrorToast(
                <div>
                    <div className="font-semibold mb-2">File Upload Errors:</div>
                    <ul className="list-disc list-inside space-y-1">
                        {errors.map((error, index) => (
                            <li key={index} className="text-sm">{error}</li>
                        ))}
                    </ul>
                </div>
            );
        }

        // Check total file count
        if (files.length + validFiles.length > maxFiles) {
            showErrorToast(`You can only upload up to ${maxFiles} files. Currently selected: ${files.length}, attempting to add: ${validFiles.length}.`);
            return;
        }

        // Add valid files
        if (validFiles.length > 0) {
            const newFilePreviews = validFiles.map(createFilePreview);
            onFilesChange([...files, ...newFilePreviews]);
        }
    }, [files, maxFiles, maxFileSize, maxFileSizeBytes, onFilesChange]);

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files);
        }
    }, [handleFileSelect]);

    const removeFile = useCallback((id: string) => {
        const newFiles = files.filter(file => file.id !== id);
        onFilesChange(newFiles);
    }, [files, onFilesChange]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files);
        e.target.value = '';
    }, [handleFileSelect]);

    const clearAll = useCallback(() => {
        // Clean up object URLs to prevent memory leaks
        files.forEach(file => {
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
        });
        onFilesChange([]);
    }, [files, onFilesChange]);

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Header */}
            <div className="space-y-2">
                <Text className="text-sm font-semibold text-gray-900">
                    Files (optional, max {maxFiles})
                </Text>
                <Text className="text-xs text-gray-500">
                    Upload images (JPEG, PNG, GIF, WEBP) or PDF files. Drag and drop or click to select.
                </Text>
            </div>

            {/* Drag and Drop Area */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${dragActive
                    ? 'border-blue-500 bg-blue-50 scale-105'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <div className="space-y-4">
                    {/* Icon */}
                    <div className="mx-auto w-16 h-16 text-gray-400">
                        {dragActive ? (
                            <svg className="w-full h-full text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        ) : (
                            <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        )}
                    </div>

                    {/* Text */}
                    <div className="space-y-2">
                        <div className="text-sm text-gray-600">
                            <label htmlFor="file-upload" className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 transition-colors">
                                <span>Choose files</span>
                                <Input
                                    id="file-upload"
                                    type="file"
                                    accept={accept}
                                    multiple
                                    onChange={handleInputChange}
                                    className="sr-only"
                                />
                            </label>
                            <Text> or drag and drop</Text>
                        </div>
                        <Text className="text-xs text-gray-500">
                            PNG, JPG, GIF, WEBP, PDF up to {maxFileSize}MB each
                        </Text>
                    </div>
                </div>
            </div>

            {/* File Previews */}
            {files.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Text className="text-sm font-medium text-gray-700">
                            Selected Files ({files.length}/{maxFiles})
                        </Text>
                        <Button
                            type="button"
                            outline
                            onClick={clearAll}
                            className="text-red-600 border-red-600 hover:bg-red-50 text-sm px-3 py-1"
                        >
                            Clear All
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {files.map((fileItem) => (
                            <div key={fileItem.id} className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                                {/* File Preview */}
                                <div className="aspect-square bg-gray-50 relative">
                                    {fileItem.type === 'image' && fileItem.preview ? (
                                        <img
                                            src={fileItem.preview}
                                            alt={fileItem.file.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center">
                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                            </div>
                                        </div>
                                    )}

                                    {/* Remove Button */}
                                    <button
                                        type="button"
                                        onClick={() => removeFile(fileItem.id)}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                                    >
                                        ×
                                    </button>

                                    {/* File Type Badge */}
                                    <div className="absolute top-2 left-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${fileItem.type === 'image'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-purple-100 text-purple-800'
                                            }`}>
                                            {fileItem.type === 'image' ? 'IMG' : 'PDF'}
                                        </span>
                                    </div>
                                </div>

                                {/* File Info */}
                                <div className="p-3">
                                    <Text className="text-xs text-gray-900 font-medium truncate" title={fileItem.file.name}>
                                        {fileItem.file.name}
                                    </Text>
                                    <div className="flex items-center justify-between mt-1">
                                        <Text className={`text-xs ${fileItem.file.size > maxFileSizeBytes ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                                        </Text>
                                        {fileItem.file.size > maxFileSizeBytes && (
                                            <span className="text-xs text-red-600 font-semibold">
                                                Too large!
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Alternative Upload Button */}
            <div className="flex justify-center">
                <label htmlFor="file-upload-alt" className="cursor-pointer">
                    <Input
                        id="file-upload-alt"
                        type="file"
                        accept={accept}
                        multiple
                        onChange={handleInputChange}
                        className="sr-only"
                    />
                    <div className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Select Files
                    </div>
                </label>
            </div>
        </div>
    );
};

export default MultipleImageUpload;
